/**
 * Round comparison: what changed between two rounds, block by block.
 *
 * The layout is the rounds mockup's: pickers and the change list on the left,
 * a two-column diff canvas on the right. The cards are simplified prose from
 * `course.json`, deliberately not the runtime — the mockup draws them the
 * same way, because the question here is "what did you change", not "how does
 * it render".
 *
 * The one line of product logic worth naming: a changed block that carries an
 * open comment from an earlier round *answers* that comment. The pill on the
 * row says whose, and turns teal once the thread is resolved.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiError, type BlockChangeWire, type DiffWire, type RoundWire } from '../api';
import { Link } from '../router';
import { Wordmark } from '../components/Wordmark';
import { display, LAYOUT, mono, monoPlain, RADIUS } from '../theme';

type KindFilter = 'all' | 'edited' | 'added' | 'removed';

const KIND_BADGE: Record<
  BlockChangeWire['kind'],
  { label: string; colour: string; bg: string }
> = {
  edited: { label: 'EDITED', colour: 'var(--primary)', bg: 'var(--primary-soft)' },
  added: { label: 'ADDED', colour: 'var(--surface-card)', bg: 'var(--primary)' },
  removed: { label: 'REMOVED', colour: 'var(--danger-500)', bg: 'rgba(217,79,111,.14)' },
  unchanged: { label: 'UNCHANGED', colour: 'var(--text-muted)', bg: 'var(--surface-sunken, transparent)' }
};

export function RoundsPage({ id }: { id: string }) {
  const [rounds, setRounds] = useState<RoundWire[]>([]);
  const [current, setCurrent] = useState(1);
  const [from, setFrom] = useState<number | null>(null);
  const [to, setTo] = useState<number | null>(null);
  const [diff, setDiff] = useState<DiffWire | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [kindFilter, setKindFilter] = useState<KindFilter>('all');
  const [onlyChanges, setOnlyChanges] = useState(true);
  const [split, setSplit] = useState(true);
  const [title, setTitle] = useState('');

  useEffect(() => {
    api.projects
      .get(id)
      .then((p) => setTitle(p.title))
      .catch(() => undefined);
    api.rounds
      .list(id)
      .then((r) => {
        setRounds(r.rounds);
        setCurrent(r.current);
        if (r.current > 1) {
          setFrom(r.current - 1);
          setTo(r.current);
        }
      })
      .catch((e) =>
        setError(e instanceof ApiError ? e.message : 'The rounds would not load.')
      );
  }, [id]);

  const loadDiff = useCallback(() => {
    if (!from || !to || from === to) return;
    setDiff(null);
    api.rounds
      .diff(id, from, to)
      .then(setDiff)
      .catch((e) =>
        setError(e instanceof ApiError ? e.message : 'That comparison would not load.')
      );
  }, [id, from, to]);

  useEffect(() => {
    loadDiff();
  }, [loadDiff]);

  const shown = useMemo(() => {
    if (!diff) return [];
    return diff.changes.filter((ch) => {
      if (kindFilter !== 'all' && ch.kind !== kindFilter) return false;
      if (onlyChanges && ch.kind === 'unchanged') return false;
      return true;
    });
  }, [diff, kindFilter, onlyChanges]);

  const hidden = diff
    ? diff.changes.filter((ch) => ch.kind === 'unchanged').length -
      shown.filter((ch) => ch.kind === 'unchanged').length
    : 0;

  const byLesson = useMemo(() => {
    const groups = new Map<string, BlockChangeWire[]>();
    for (const ch of shown) {
      groups.set(ch.lessonTitle, [...(groups.get(ch.lessonTitle) ?? []), ch]);
    }
    return [...groups.entries()];
  }, [shown]);

  if (rounds.length < 2) {
    return (
      <Shell id={id} title={title}>
        <div style={{ padding: '80px 32px', textAlign: 'center' }}>
          <h1 style={display(24)}>
            {error ?? 'There is only one round so far.'}
          </h1>
          <p style={{ ...monoPlain, marginTop: 10 }}>
            Send the course again from Compose or Cloud to start round {current + 1}.
          </p>
          <p style={{ marginTop: 14 }}>
            <Link to={`/p/${id}`}>Back to comments</Link>
          </p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell id={id} title={title}>
      <div style={{ display: 'flex', flex: 1, minHeight: 0, gap: LAYOUT.gutter }}>
        {/* ── left: pickers, stats, the change list ─────────────────────── */}
        <aside
          style={{
            width: LAYOUT.sidebar,
            flex: 'none',
            background: 'var(--app-chrome-panel)',
            borderRadius: RADIUS.appPanel,
            overflowY: 'auto',
            padding: '14px 0'
          }}
        >
          <p style={{ ...mono, padding: '0 16px 10px' }}>Compare</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 16px' }}>
            {([['from', from, setFrom], ['to', to, setTo]] as const).map(([which, value, set], i) => (
              <span key={which} style={{ display: 'contents' }}>
                {i === 1 && <span style={{ color: 'var(--text-muted)' }}>→</span>}
                <select
                  value={value ?? ''}
                  onChange={(e) => set(Number(e.target.value))}
                  aria-label={which === 'from' ? 'Compare from' : 'Compare to'}
                  style={{
                    flex: 1,
                    padding: '7px 8px',
                    borderRadius: RADIUS.control,
                    border: `1px solid ${which === 'to' ? 'var(--primary)' : 'var(--border-subtle)'}`,
                    background: 'var(--surface-sunken, transparent)',
                    color: 'var(--text-strong)',
                    font: 'inherit',
                    fontSize: 13
                  }}
                >
                  {rounds.map((r) => (
                    <option key={r.round} value={r.round}>
                      Round {r.round}
                      {r.closedAt ? '' : ' · live'}
                    </option>
                  ))}
                </select>
              </span>
            ))}
          </div>

          {diff && (
            <>
              <div style={{ display: 'flex', gap: 18, padding: '16px 16px 4px' }}>
                {(
                  [
                    ['edited', diff.stats.edited, 'var(--text-strong)'],
                    ['added', diff.stats.added, 'var(--text-strong)'],
                    ['removed', diff.stats.removed, 'var(--danger-500)']
                  ] as const
                ).map(([label, n, colour]) => (
                  <span key={label}>
                    <span style={{ ...display(22), color: colour }}>{n}</span>
                    <span style={{ ...mono, fontSize: 8.5, display: 'block' }}>
                      {label.toUpperCase()}
                    </span>
                  </span>
                ))}
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, padding: '10px 16px' }}>
                {(['all', 'edited', 'added', 'removed'] as const).map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setKindFilter(k)}
                    style={{
                      ...mono,
                      fontSize: 8.5,
                      padding: '3px 8px',
                      borderRadius: RADIUS.chip,
                      cursor: 'pointer',
                      border: `1px solid ${kindFilter === k ? 'var(--primary)' : 'var(--border-subtle)'}`,
                      background: kindFilter === k ? 'var(--primary-soft)' : 'none',
                      color: kindFilter === k ? 'var(--primary)' : 'var(--text-muted)'
                    }}
                  >
                    {k === 'all' ? 'ALL CHANGES' : k.toUpperCase()}
                  </button>
                ))}
              </div>

              {byLesson.map(([lesson, changes]) => (
                <div key={lesson} style={{ padding: '8px 0' }}>
                  <p style={{ ...monoPlain, fontSize: 8.5, padding: '0 16px 4px' }}>
                    {lesson.toUpperCase()}
                  </p>
                  {changes.map((ch) => (
                    <a
                      key={ch.blockId}
                      href={`#chg-${ch.blockId}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '6px 16px',
                        textDecoration: 'none',
                        borderLeft: `3px solid ${
                          ch.kind === 'removed' ? 'var(--danger-500)' : 'var(--primary)'
                        }`
                      }}
                    >
                      <span
                        style={{
                          flex: 1,
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: 12.5,
                          color: 'var(--text-body)'
                        }}
                      >
                        {ch.heading || ch.blockKind || 'Block'}
                      </span>
                      <span style={{ ...mono, fontSize: 7.5, color: KIND_BADGE[ch.kind].colour }}>
                        {KIND_BADGE[ch.kind].label}
                      </span>
                    </a>
                  ))}
                </div>
              ))}

              {diff.carriedOver > 0 && (
                <div
                  style={{
                    margin: '10px 12px',
                    padding: '10px 12px',
                    borderRadius: RADIUS.control,
                    border: '1px solid #e8a33d',
                    fontSize: 12,
                    lineHeight: 1.5,
                    color: 'var(--text-body)'
                  }}
                >
                  {diff.carriedOver} comment{diff.carriedOver === 1 ? '' : 's'} from earlier
                  rounds {diff.carriedOver === 1 ? 'is' : 'are'} still open.{' '}
                  <Link to={`/p/${id}`}>Review the open threads</Link>
                </div>
              )}
            </>
          )}
        </aside>

        {/* ── right: the diff canvas ────────────────────────────────────── */}
        <main
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            background: 'var(--app-panel)',
            borderRadius: RADIUS.appPanel,
            overflow: 'hidden'
          }}
        >
          {/* A toolbar inside the panel. */}
          <div
            style={{
              flex: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 18px',
              background: 'var(--app-panel-alt)'
            }}
          >
            <span style={mono}>
              Round {from} → round {to}
            </span>
            <span style={{ flex: 1 }} />
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={onlyChanges}
                onChange={(e) => setOnlyChanges(e.target.checked)}
              />
              <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Only show changes</span>
            </label>
            {(
              [
                [true, 'Side by side'],
                [false, `Round ${to} only`]
              ] as const
            ).map(([v, label]) => (
              <button
                key={label}
                type="button"
                onClick={() => setSplit(v)}
                style={{
                  ...mono,
                  fontSize: 9,
                  padding: '4px 9px',
                  borderRadius: RADIUS.chip,
                  cursor: 'pointer',
                  border: `1px solid ${split === v ? 'var(--primary)' : 'var(--border-subtle)'}`,
                  background: split === v ? 'var(--primary-soft)' : 'none',
                  color: split === v ? 'var(--primary)' : 'var(--text-muted)'
                }}
              >
                {label}
              </button>
            ))}
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '18px 22px' }}>
            {!diff && !error && <p style={monoPlain}>Comparing</p>}
            {error && <p style={{ color: 'var(--danger-500)' }}>{error}</p>}

            {diff && split && (
              <div
                style={{
                  position: 'sticky',
                  top: -18,
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 14,
                  padding: '6px 0',
                  // Opaque, because the change list scrolls under it — and now
                  // it is the panel it sits on rather than the window ground.
                  background: 'var(--app-panel)',
                  zIndex: 2
                }}
              >
                <span style={mono}>ROUND {from} · CLOSED</span>
                <span style={{ ...mono, color: 'var(--primary)' }}>
                  ROUND {to}
                  {to === current ? ' · LIVE' : ''}
                </span>
              </div>
            )}

            {hidden > 0 && (
              <p style={{ ...monoPlain, margin: '6px 0 12px' }}>
                {hidden} unchanged block{hidden === 1 ? '' : 's'} hidden.{' '}
                <button
                  type="button"
                  onClick={() => setOnlyChanges(false)}
                  style={{ font: 'inherit', border: 0, background: 'none', color: 'var(--primary)', cursor: 'pointer', padding: 0 }}
                >
                  Show all blocks
                </button>
              </p>
            )}

            {shown.map((ch) => (
              <div key={ch.blockId} id={`chg-${ch.blockId}`} style={{ marginBottom: 18 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ ...monoPlain, fontSize: 8.5 }}>
                    {ch.lessonTitle.toUpperCase()}
                    {ch.heading ? ` · ${ch.heading.toUpperCase()}` : ''}
                  </span>
                  <span
                    style={{
                      ...mono,
                      fontSize: 7.5,
                      padding: '2px 6px',
                      borderRadius: RADIUS.chip,
                      color: KIND_BADGE[ch.kind].colour,
                      background: KIND_BADGE[ch.kind].bg
                    }}
                  >
                    {KIND_BADGE[ch.kind].label}
                  </span>
                  {ch.answers.map((a) => (
                    <span
                      key={a.threadId}
                      title={a.resolved ? 'That thread is resolved' : 'That thread is still open'}
                      style={{
                        ...mono,
                        fontSize: 7.5,
                        padding: '2px 6px',
                        borderRadius: RADIUS.chip,
                        color: a.resolved ? 'var(--signal)' : 'var(--primary)',
                        border: `1px solid ${a.resolved ? 'var(--signal)' : 'var(--primary)'}`
                      }}
                    >
                      {a.resolved ? '✓ ' : ''}ANSWERS {a.who.split(' ')[0]?.toUpperCase()}’S ROUND{' '}
                      {a.round} COMMENT
                    </span>
                  ))}
                </div>

                <div
                  style={
                    split
                      ? { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }
                      : { display: 'block' }
                  }
                >
                  {split && (
                    <DiffCard
                      text={ch.before}
                      tone="before"
                      emptyLabel={`NOT IN ROUND ${from}`}
                    />
                  )}
                  <DiffCard
                    text={ch.after}
                    tone={ch.kind === 'removed' ? 'removed' : 'after'}
                    emptyLabel={`REMOVED IN ROUND ${to}`}
                  />
                </div>
              </div>
            ))}

            {diff && shown.length === 0 && (
              <p style={monoPlain}>Nothing matches those filters.</p>
            )}
          </div>

          <footer
            style={{
              height: LAYOUT.statusStrip,
              flex: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '0 14px',
              background: 'var(--app-panel-alt)'
            }}
          >
            {diff && (
              <>
                <span style={mono}>
                  Comparing round {from} with round {to}
                  {to === current ? ' (live)' : ''}
                </span>
                <span style={{ flex: 1 }} />
                <span style={mono}>
                  {diff.stats.edited + diff.stats.added + diff.stats.removed} blocks changed
                </span>
                {diff.priorThreads > 0 && (
                  <span style={mono}>
                    {diff.answered} of {diff.priorThreads} earlier comments answered
                  </span>
                )}
                {diff.carriedOver > 0 && <span style={mono}>{diff.carriedOver} carried over</span>}
              </>
            )}
          </footer>
        </main>
      </div>
    </Shell>
  );
}

function DiffCard({
  text,
  tone,
  emptyLabel
}: {
  text: string | null;
  tone: 'before' | 'after' | 'removed';
  emptyLabel: string;
}) {
  if (text === null) {
    return (
      <div
        style={{
          border: `1px dashed ${tone === 'removed' ? 'var(--danger-500)' : 'var(--border-strong, var(--border-subtle))'}`,
          borderRadius: RADIUS.card,
          padding: '18px 16px',
          display: 'grid',
          placeItems: 'center'
        }}
      >
        <span style={{ ...mono, fontSize: 8.5, color: tone === 'removed' ? 'var(--danger-500)' : 'var(--text-muted)' }}>
          {emptyLabel}
        </span>
      </div>
    );
  }
  return (
    <div
      style={{
        background: tone === 'before' ? 'var(--surface-sunken, transparent)' : 'var(--surface-card)',
        opacity: tone === 'before' ? 0.72 : 1,
        border: '1px solid var(--border-subtle)',
        borderLeft: `3px solid ${tone === 'removed' ? 'var(--danger-500)' : 'var(--primary)'}`,
        borderRadius: RADIUS.card,
        padding: '12px 14px',
        fontSize: 13,
        lineHeight: 1.6,
        color: 'var(--text-body)',
        whiteSpace: 'pre-wrap'
      }}
    >
      {text || <span style={monoPlain}>No prose in this block.</span>}
    </div>
  );
}

/** The same chrome as the workspace, stripped to the way back: the violet
 *  ground, panels on it, and a bar with no divider. */
function Shell({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  const G = LAYOUT.gutter;
  return (
    <div
      className="mv-app"
      style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        gap: G,
        padding: G,
        background: 'var(--app-ground)',
        overflow: 'hidden'
      }}
    >
      <header
        style={{
          height: LAYOUT.topbar,
          flex: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '0 8px'
        }}
      >
        <Link to="/">
          <Wordmark reversed size={15} />
        </Link>
        {title && (
          <>
            <span style={{ width: 1, height: 18, background: 'var(--border-subtle)' }} />
            <span style={{ color: 'var(--text-strong)', fontSize: 13 }}>{title}</span>
          </>
        )}
        <span style={{ flex: 1 }} />
        <Link to={`/p/${id}`} style={{ ...mono, fontSize: 10, color: 'var(--text-muted)' }}>
          BACK TO COMMENTS
        </Link>
      </header>
      {children}
    </div>
  );
}
